import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DeepPartialSkipArrayKey,
  FieldValues,
  UseFormTrigger,
  useFormState,
  useWatch
} from "react-hook-form";

import type { SxProps } from "@mui/material/styles";

import { datadogLogs } from "@datadog/browser-logs";
import { isEqual } from "lodash";
import { v4 as uuidv4 } from "uuid";

import DataSaveState, { DataSaveStateStatus } from "components/data-save-state";
import { AUTOSAVE_CROSS_TAB_COORDINATION } from "constants/feature-flags";
import useDebounce from "hooks/use-debounce.hook";

// Global save coordination across all hook instances
type PendingItem = { data: any; timestamp: number } | null;
type GroupState = {
  inFlight: boolean;
  pending: PendingItem;
  refs: number;
  deleteWhenIdle?: boolean;
};
const saveGroups = new Map<string, GroupState>();
const getGroupState = (key: string): GroupState => {
  if (!saveGroups.has(key)) {
    saveGroups.set(key, { inFlight: false, pending: null, refs: 0 });
  }
  return saveGroups.get(key)!;
};

// `groupKey` coalesces a form against ITSELF. `serializeKey` serializes forms against
// EACH OTHER when they write the same backing row.
//
// The audiology report is eleven separate autosaving forms — audiogram, tympanometry,
// speech-in-noise, reflex decay, otoscopy, summary, diagnosis, ... — and every one of
// them saves through the same `PATCH /api/reports/<id>/`. Each has its own `groupKey`,
// so each is correctly prevented from stacking on itself, and nothing prevented all
// eleven from having a request in flight against the same row at once. The backend takes
// `select_for_update` on that row for the whole request under `ATOMIC_REQUESTS=True`, so
// concurrent sections do not merge — they queue, each one holding a gunicorn worker for
// its entire wait. On 2026-08-27 that turned one audiologist filling in a reflex grid
// into ~100 sessions serialised through a single tuple lock and 70-95% of the web fleet
// blocked for six minutes (ENG-1849).
//
// Serialising here does not change WHAT is sent, only WHEN: the same requests, one at a
// time. An uncontended PATCH is ~100ms, so eleven sections cost about a second in the
// worst case, against 40s per request when they contend.
//
// SCOPE: `rowLocks` is a module-level map, so this serialises within ONE browser tab. The
// key names the row; the enforcement is per-tab. Behind AUTOSAVE_CROSS_TAB_COORDINATION
// (ENG-1873), `runUnderCrossTabLock` below extends the ordering to every tab of the same
// browser via the Web Locks API. Neither reaches across browsers or users — nine
// clinicians on one report still produce nine concurrent PATCHes; a server-side cap is
// the backstop for that case (ENG-1854).
const SERIALIZE_WAIT_CEILING_MS = 30_000;

// A 409 `row_locked` is the backend telling us the row was busy and NOTHING was written —
// `BoundedRowLockMixin` rolls the transaction back before any save (suno-backend
// core/views.py). It is the success case of the ENG-1695 guard, not a failure: the
// alternative was queuing for the holder's whole lifetime and holding a worker for it.
// Surfacing it as "Save failed" would train users to distrust a working safety valve, so
// take the retry the backend is inviting. Exactly one, jittered — this replaces a request
// that already failed fast, so it adds no sustained load, and an unbounded retry on a
// contended row is how a convoy feeds itself.
const ROW_LOCKED_RETRY_DELAY_MS = 600;
const ROW_LOCKED_RETRY_JITTER_MS = 400;

const isRowLockedResponse = (response: any): boolean =>
  response?.error?.status === 409 &&
  response?.error?.data?.code === "row_locked";

const rowLockedRetryDelay = () =>
  ROW_LOCKED_RETRY_DELAY_MS + Math.random() * ROW_LOCKED_RETRY_JITTER_MS;

type RowLock = { tail: Promise<void>; waiting: number };
const rowLocks = new Map<string, RowLock>();

// `rowLocks` above serialises within ONE tab; this extends the same ordering across every
// tab of the origin via the Web Locks API when AUTOSAVE_CROSS_TAB_COORDINATION is on. The
// browser queues `navigator.locks.request` callers on `key` exactly like our tail chain
// queues same-tab callers. A wedged holder in another tab must not freeze this tab's save
// UI, so on timeout we abort the request and degrade to the per-tab-only behaviour — the
// pre-flag status quo, not a failure.
//
// `remainingWaitMs` is what is LEFT of SERIALIZE_WAIT_CEILING_MS after the same-tab tail
// wait, not a fresh ceiling. The two waits are sequential, so giving each its own 30s let
// a save sit for a full minute before its request was even issued — twice the bound the
// ceiling exists to promise, and a stuck "Saving..." on a clinical report is the exact
// thing it is there to prevent. A budget already spent means take the degraded path now.
const runUnderCrossTabLock = async <T,>(
  key: string,
  run: () => Promise<T>,
  remainingWaitMs: number
): Promise<T> => {
  if (remainingWaitMs <= 0) {
    return run();
  }
  const controller = new AbortController();
  const ceiling = setTimeout(() => controller.abort(), remainingWaitMs);
  try {
    return await navigator.locks.request(
      key,
      { signal: controller.signal },
      async () => {
        // Granted — the ceiling only guards the wait, never the save itself.
        clearTimeout(ceiling);
        return run();
      }
    );
  } catch (error) {
    if (controller.signal.aborted) {
      return run();
    }
    throw error;
  } finally {
    clearTimeout(ceiling);
  }
};

// Deliberately NOT an async function, and the no-key path returns `run()` directly rather
// than awaiting it. An `async` wrapper would add a microtask tick to every save in the app,
// including the ~13 forms that pass no `serializeKey` at all — a shared hook with 17
// consumers should be bit-identical for everyone who has not opted in.
const withRowLock = <T,>(
  key: string | undefined,
  run: () => Promise<T>,
  crossTab: boolean
): Promise<T> => (key ? serializeOnKey(key, run, crossTab) : run());

const serializeOnKey = async <T,>(
  key: string,
  run: () => Promise<T>,
  crossTab: boolean
): Promise<T> => {
  // ONE budget for the whole wait. The tail wait below and the cross-tab lock draw from
  // it in sequence; `execute` is only ever called after the tail wait settles, so reading
  // the clock here gives the lock whatever is left rather than a second full ceiling.
  const waitDeadline = Date.now() + SERIALIZE_WAIT_CEILING_MS;
  const execute =
    crossTab && typeof navigator !== "undefined" && navigator.locks
      ? () => runUnderCrossTabLock(key, run, waitDeadline - Date.now())
      : run;

  const lock = rowLocks.get(key) ?? { tail: Promise.resolve(), waiting: 0 };
  rowLocks.set(key, lock);
  lock.waiting += 1;

  // Claim our slot synchronously, before any await, so concurrent callers chain in
  // arrival order rather than all reading the same tail.
  const previous = lock.tail;
  let release!: () => void;
  lock.tail = new Promise<void>((resolve) => {
    release = resolve;
  });

  let ceiling: ReturnType<typeof setTimeout> | undefined;
  try {
    // A request that never settles must not freeze every other section's save UI for the
    // rest of the session — a stuck "Saving..." on a clinical report is worse than the
    // contention this avoids. On the ceiling we proceed anyway, degrading to the old
    // concurrent behaviour rather than deadlocking. The wedged link still releases later.
    await Promise.race([
      previous,
      new Promise<void>((resolve) => {
        ceiling = setTimeout(resolve, SERIALIZE_WAIT_CEILING_MS);
      })
    ]);
    return await execute();
  } finally {
    // The loser of the race is still pending; without this every save leaves a 30s timer
    // alive behind it.
    if (ceiling !== undefined) clearTimeout(ceiling);
    release();
    lock.waiting -= 1;
    if (lock.waiting === 0 && rowLocks.get(key) === lock) {
      rowLocks.delete(key);
    }
  }
};

export default function useFormAutoSave({
  control,
  updateData,
  formatter,
  allowSavingInvalidForm = false,
  delay,
  reset,
  sx,
  afterUpdate,
  skipUpdateComparator,
  trigger,
  dataDogLabel,
  groupKey = "autosave-global",
  serializeKey,
  disableAutoSave = false
}: {
  control: any;
  updateData: any;
  formatter?: Function;
  allowSavingInvalidForm?: boolean;
  delay?: number;
  reset?: any;
  sx?: SxProps;
  afterUpdate?: () => void;
  skipUpdateComparator?: (newData: any) => boolean;
  trigger?: UseFormTrigger<any>;
  dataDogLabel?: string;
  groupKey?: string;
  /**
   * Identifies the backing row this form writes to (e.g. `report-1029731`). Forms sharing
   * one `serializeKey` send their saves one at a time instead of all at once. Set it on
   * every form that PATCHes the same record — see the note above `withRowLock`.
   */
  serializeKey?: string;
  disableAutoSave?: boolean;
}) {
  const [autosaveIsTriggered, setAutosaveIsTriggered] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastChanged, setLastChanged] = useState(Date.now());
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  const [dataState, setDataState] = useState<DataSaveStateStatus>("saved");
  const [hasError, setHasError] = useState(false);
  // Prevent overlapping saves within the same render tick and skip duplicate saves
  const isSavingRef = useRef(false);
  const lastSavedTimestampRef = useRef<number>(0);
  // The formatted payload of this instance's last SUCCESSFUL save. A save whose
  // payload deep-equals it is not re-sent — forced triggers (the report
  // sections' Clear buttons call triggerAutosave() on every click, changed or
  // not) and transient value churn bump lastChanged without changing the
  // payload, which is how the 2026-08-27 replay produced the same body PATCHed
  // 3+ times in a row with no intervening edit (ENG-1873).
  const lastSavedRequestDataRef = useRef<unknown>(undefined);
  const mountedRef = useRef(true);
  const flags = useFlags();
  const crossTabCoordination = Boolean(flags[AUTOSAVE_CROSS_TAB_COORDINATION]);

  const formState = useFormState({ control });
  const formData = useWatch({ control });
  const { value: debouncedFormData, isDebouncing } = useDebounce(
    formData,
    delay
  );

  const dataSaveStateContent = useMemo(
    () => <DataSaveState state={dataState} sx={sx} />,
    [dataState, sx]
  );

  // Track mount state and manage group reference counting for cleanup
  useEffect(() => {
    mountedRef.current = true;
    const group = getGroupState(groupKey);
    group.refs = (group.refs ?? 0) + 1;
    return () => {
      mountedRef.current = false;
      const currentGroup = getGroupState(groupKey);
      currentGroup.refs = Math.max(0, (currentGroup.refs ?? 1) - 1);
      if (currentGroup.refs === 0) {
        if (currentGroup.inFlight) {
          // Mark for deletion once the current in-flight operation completes
          currentGroup.deleteWhenIdle = true;
          currentGroup.pending = null;
        } else {
          saveGroups.delete(groupKey);
        }
      }
    };
  }, [groupKey]);

  useEffect(() => {
    if (hasError) {
      return;
    }

    const shouldSetDirty =
      autosaveIsTriggered ||
      (formState.isDirty &&
        !skipUpdateComparator?.(formData) &&
        lastChanged > lastUpdated &&
        dataState !== "dirty");

    if (shouldSetDirty) {
      setDataState("dirty");
      return;
    }
    if (!formState.isDirty && dataState === "dirty") {
      setLastUpdated(Date.now());
      setDataState("saved");
    }
  }, [
    formState.isDirty,
    formData,
    skipUpdateComparator,
    lastUpdated,
    lastChanged,
    dataState,
    autosaveIsTriggered,
    hasError
  ]);

  const handleAutoSave = useCallback(
    async (data: DeepPartialSkipArrayKey<FieldValues>, timestamp: number) => {
      const trn_id = uuidv4();
      const url = window.location.href;
      // GUARDS LIVE OUTSIDE THE TRY, AND THAT PLACEMENT IS THE FIX for the
      // incident's concurrent-burst mechanism (ENG-1873): the finally below
      // unconditionally clears group.inFlight and replays group.pending, which
      // is only correct for the ONE invocation that claimed the save. When the
      // guard returns sat inside the try, a call arriving mid-save took the
      // coalesce return, fell into the finally, released the in-flight lock it
      // never held, and immediately replayed the pending it had just stored —
      // launching a concurrent duplicate of a save already on the wire. No
      // guard below can throw, so nothing is lost by exiting before the try.
      //
      // Skip if a save is already in progress or we already saved this exact change
      if (isSavingRef.current || lastSavedTimestampRef.current === timestamp) {
        return;
      }
      const group = getGroupState(groupKey);
      // If there are no active listeners for this group, skip saving
      if (group.refs === 0) {
        return;
      }
      if (group.inFlight) {
        // coalesce concurrent saves in the same group – keep only the latest
        group.pending = { data, timestamp };
        return;
      }

      // Claim the save. From here on the finally's teardown is ours to run.
      group.inFlight = true;
      isSavingRef.current = true;
      if (mountedRef.current) {
        setIsUpdating(true);
      }
      try {
        const requestData = formatter ? formatter(data) : data;
        if (
          lastSavedRequestDataRef.current !== undefined &&
          isEqual(lastSavedRequestDataRef.current, requestData)
        ) {
          // The server already holds exactly this — treat it as saved without a
          // request. afterUpdate still fires so "save & continue" flows behave
          // as if the (no-op) save happened. Returning releases the claim via
          // the finally, which also replays anything that coalesced meanwhile.
          if (mountedRef.current) {
            setDataState("saved");
            setHasError(false);
            setLastUpdated(timestamp);
          }
          lastSavedTimestampRef.current = timestamp;
          afterUpdate?.();
          return;
        }

        const start = performance.now();
        if (dataDogLabel) {
          datadogLogs.logger.log(`useFormAutoSave ${dataDogLabel} request`, {
            state: "pending",
            type: "start",
            url,
            trn_id,
            started_at: new Date().getTime(),
            request: requestData
          });
        }

        const queuedAt = performance.now();
        let response = await withRowLock(
          serializeKey,
          () => updateData(requestData),
          crossTabCoordination
        );
        let rowLockedRetried = false;
        if (isRowLockedResponse(response)) {
          rowLockedRetried = true;
          await new Promise((resolve) =>
            setTimeout(resolve, rowLockedRetryDelay())
          );
          response = await withRowLock(
            serializeKey,
            () => updateData(requestData),
            crossTabCoordination
          );
        }
        const end = performance.now();
        // Time spent waiting for a sibling form's save, separated from the request itself,
        // so a rise in serialisation is visible before it becomes a latency complaint.
        const queued_ms = queuedAt - start;
        // RTK Query mutation triggers called without `.unwrap()` (as `updateData` is
        // here) NEVER reject on an HTTP error — they resolve to `{ data }` or
        // `{ error }`. Without this check, a 409 (or any other API error) from a
        // contended autosave was falling straight into the "success" branch below:
        // the UI would show "Saved" and clear dirty state while nothing was
        // persisted. The `catch` block further down only ever caught a genuine JS
        // exception (network-level throw before RTK resolves), which is why this
        // shipped unnoticed — an HTTP error response never took that path.
        const isError = Boolean(response && "error" in response);
        if (dataDogLabel) {
          datadogLogs.logger.log(`useFormAutoSave ${dataDogLabel} request`, {
            state: isError ? "failure" : "success",
            type: "end",
            url,
            trn_id,
            finished_at: new Date().getTime(),
            execution_ms: end - start,
            queued_ms,
            row_locked_retried: rowLockedRetried,
            request: requestData,
            response
          });
        }
        if (isError) {
          if (mountedRef.current) {
            setDataState("error");
            setHasError(true);
            setLastUpdated(timestamp);
          }
          console.error("Auto-save failed:", (response as any)?.error);
          return;
        }
        if (mountedRef.current) {
          setDataState("saved");
          setHasError(false);
          setLastUpdated(timestamp);
        }
        lastSavedTimestampRef.current = timestamp;
        lastSavedRequestDataRef.current = requestData;
        afterUpdate?.();
      } catch (error: any) {
        if (dataDogLabel) {
          datadogLogs.logger.error(`useFormAutoSave ${dataDogLabel} request`, {
            state: "failure",
            type: "end",
            url,
            trn_id,
            finished_at: new Date().getTime(),
            request: data,
            error: error?.message ?? error,
            stack: error?.stack
          });
        }
        if (mountedRef.current) {
          setDataState("error");
          setHasError(true);
          setLastUpdated(timestamp);
        }
        console.error("Auto-save failed:", error);
      } finally {
        const group = getGroupState(groupKey);
        const next = group.pending;
        group.pending = null;
        group.inFlight = false;
        isSavingRef.current = false;
        if (mountedRef.current) {
          setIsUpdating(false);
        }
        // If no more listeners for this group and nothing pending, prune the group entry
        if (
          group.deleteWhenIdle &&
          group.refs === 0 &&
          !group.pending &&
          !group.inFlight
        ) {
          saveGroups.delete(groupKey);
        }
        // If there was a pending save while the current one was in-flight, run it now
        if (next) {
          // schedule on the macrotask queue to avoid deep recursion
          if (group.refs > 0) {
            setTimeout(() => {
              handleAutoSave(next.data, next.timestamp);
            }, 0);
          }
        }
      }
    },
    [
      groupKey,
      serializeKey,
      crossTabCoordination,
      formatter,
      dataDogLabel,
      updateData,
      afterUpdate
    ]
  );

  useEffect(() => {
    if (!isEqual(debouncedFormData, formData)) {
      setLastChanged(Date.now());
      setHasError(false);
    }
  }, [debouncedFormData, formData]);

  useEffect(() => {
    if (disableAutoSave) return;
    const isFormValid = formState.isValid || allowSavingInvalidForm;
    const isLatestData = isEqual(debouncedFormData, formData);
    const shouldSave =
      (autosaveIsTriggered && !formState.isValidating) ||
      (!formState.isValidating &&
        formState.isDirty &&
        isLatestData &&
        !isUpdating &&
        !isSavingRef.current &&
        lastChanged > lastUpdated);

    if (!shouldSave) return;

    if (!isFormValid) {
      if (dataState !== "error") {
        trigger?.();
      }

      setDataState("error");
      setHasError(true);
      return;
    }

    if (!autosaveIsTriggered && skipUpdateComparator?.(formData)) {
      setDataState("saved");
      setHasError(false);
      setLastUpdated(lastChanged);
      return;
    }

    const dataToSave = autosaveIsTriggered ? formData : debouncedFormData;
    handleAutoSave(dataToSave, lastChanged);
    setAutosaveIsTriggered(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formState.isDirty,
    formState.isValid,
    formState.isValidating,
    debouncedFormData,
    formData,
    isUpdating,
    allowSavingInvalidForm,
    skipUpdateComparator,
    lastUpdated,
    lastChanged,
    autosaveIsTriggered,
    handleAutoSave,
    trigger,
    disableAutoSave
  ]);

  useEffect(() => {
    const isLatestData = isEqual(debouncedFormData, formData);
    const finishedAllUpdates =
      lastChanged <= lastUpdated &&
      formState.isDirty &&
      formState.isValid &&
      isLatestData;

    if (reset && finishedAllUpdates) {
      reset(debouncedFormData);
    }
  }, [
    reset,
    debouncedFormData,
    formState.isDirty,
    lastUpdated,
    lastChanged,
    formData,
    formState.isValid
  ]);

  const triggerAutosave = useCallback(() => {
    setAutosaveIsTriggered(true);
  }, []);

  const saveImmediately = useCallback(async () => {
    const isFormValid = formState.isValid || allowSavingInvalidForm;

    if (!isFormValid) {
      trigger?.();
      setDataState("error");
      setHasError(true);
      return;
    }

    if (skipUpdateComparator?.(formData)) {
      setDataState("saved");
      setHasError(false);
      setLastUpdated(Date.now());
      return;
    }

    await handleAutoSave(formData, Date.now());
  }, [
    formState.isValid,
    allowSavingInvalidForm,
    trigger,
    skipUpdateComparator,
    formData,
    handleAutoSave
  ]);

  return {
    dataSaveStateContent,
    dataState,
    isUpdating,
    isDebouncing,
    isDirty: formState.isDirty,
    triggerAutosave,
    saveImmediately
  };
}
